import React, { useState, useEffect } from 'react';
import { StepWrapper } from '../StepWrapper';
import { BookingTypeSetup } from '@/components/settings/BookingTypeSetup';
import { useScreenData } from '@/hooks/useScreenData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { toast } from 'sonner';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import type { BookingType } from '@/types';
import { Loader2 } from 'lucide-react';

interface StepBookingTypesProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const parseBool = (val: unknown): boolean => {
  if (val === true || val === 'true' || val === 1 || val === '1') return true;
  return false;
};

export function StepBookingTypes({ onNext, onBack, onSkip }: StepBookingTypesProps) {
  const { settings, resources, bookings, updateSettings, currentMode } = useScreenData('settings');
  const { isLoading: isContextLoading, demoBookingTypeUsages, setDemoBookingTypeUsages } = useScheduler();

  const migratedBookingTypes = (settings.bookingTypes ?? []).map((bt) => {
    const fieldConfigs = bt.fieldConfigurations || generateDefaultFieldConfigurations();
    const tabConfigs = bt.tabConfigurations || generateDefaultTabConfigurations();
    const updatedFields = fieldConfigs.map((field) => {
      if (field.showOnCustomTab === undefined) {
        return { ...field, showOnCustomTab: false };
      }
      return field;
    });
    return {
      ...bt,
      id: String(bt.id),
      fieldConfigurations: updatedFields,
      tabConfigurations: tabConfigs,
    };
  });

  return (
    <StepWrapper
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
      nudgeMessage="Booking types define what gets scheduled and what information is captured. Set up your most common types now — you can add more at any time."
    >
      {/*
        BookingTypeSetup uses `h-full flex flex-col` at its root.
        Give it a min-height so it renders correctly without a fixed parent height.
        position:relative is needed for the loading overlay.
      */}
      <div className="relative min-h-[600px] overflow-hidden">
        {currentMode !== 'demo' && isContextLoading && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-3 bg-background border rounded-lg px-5 py-3 shadow-lg">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm font-medium">Loading Booking Types...</span>
            </div>
          </div>
        )}
        <BookingTypeSetup
          bookingTypes={migratedBookingTypes as any}
          bookings={bookings}
          resources={resources.filter((r) => String(r.id) !== 'unallocated')}
          onSave={async (bookingType) => {
            await updateSettings({ bookingTypes: [bookingType] as any });
          }}
          onDelete={(id) => {
            if (migratedBookingTypes.length <= 1) {
              toast.error('Cannot delete the last booking type');
              return;
            }
            const updated = migratedBookingTypes.filter(
              (bt) => String(bt.id) !== String(id)
            ) as any;
            updateSettings({ bookingTypes: updated });
          }}
          dataMode={currentMode}
          demoBookingTypeUsages={demoBookingTypeUsages}
          onDemoUsageChange={(bookingTypeId, resourceId, level) => {
            setDemoBookingTypeUsages((prev) => {
              const filtered = prev.filter(
                (u) =>
                  !(
                    String(u.bookingTypeId) === bookingTypeId &&
                    String(u.resourceId) === resourceId
                  )
              );
              if (level === 'none') return filtered;
              return [
                ...filtered,
                {
                  id: Date.now(),
                  resourceId: resourceId as unknown as number,
                  bookingTypeId: bookingTypeId as unknown as number,
                  isReadOnly: level === 'read-only',
                  isFullAccess: level === 'full-access',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
              ];
            });
          }}
          onDemoBulkUsageChange={(bookingTypeId, resourceIds, level) => {
            setDemoBookingTypeUsages((prev) => {
              const filtered = prev.filter(
                (u) =>
                  !(
                    String(u.bookingTypeId) === bookingTypeId &&
                    resourceIds.includes(String(u.resourceId))
                  )
              );
              if (level === 'none') return filtered;
              return [
                ...filtered,
                ...resourceIds.map((rid, idx) => ({
                  id: Date.now() + idx,
                  resourceId: rid as unknown as number,
                  bookingTypeId: bookingTypeId as unknown as number,
                  isReadOnly: level === 'read-only',
                  isFullAccess: level === 'full-access',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                })),
              ];
            });
          }}
        />
      </div>
    </StepWrapper>
  );
}
